import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { FolderOpen, Building, Users, UserCheck, Percent, Settings } from 'lucide-react';
import { useUserPrivileges } from '@/context/UserPrivilegesContext';
import { ADMIN_CARD_PRIVILEGE } from '@/lib/privilegeConstants';
import { authenticatedFetch } from '@/lib/api';

const AdminCard = ({ title, description, icon: Icon, onClick, badge }: {
  title: string;
  description: string;
  icon: any;
  onClick: () => void;
  badge?: number | null;
}) => {
  const isCoupons = title === 'Coupons';
  return (
    <Card 
      className="group cursor-pointer bg-white border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300 min-h-[120px] sm:min-h-[140px] md:min-h-[160px] active:scale-95 active:shadow-lg"
      onClick={onClick}
    >
      <CardHeader className="flex flex-row items-start justify-between p-3 sm:p-4 md:p-6 pb-3 sm:pb-4 space-y-0">
        <div className="space-y-1 sm:space-y-2 flex-1 pr-2">
          <div className="flex items-center gap-2 flex-wrap">
            <CardTitle className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 leading-tight">
              {title}
            </CardTitle>
            {badge != null && (
              <Badge variant="secondary" className="bg-teal-100 text-teal-800 border-teal-200 text-xs font-medium">
                {badge}
              </Badge>
            )}
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-500 leading-snug">
            {description}
          </p>
        </div>
        <div className={`w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-lg flex items-center justify-center text-white flex-shrink-0 ${isCoupons ? 'bg-teal-500' : 'bg-blue-500'}`}>
          <div className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6">
            <Icon className="w-full h-full" />
          </div>
        </div>
      </CardHeader>
    </Card>
  );
};

const AdministratorTab = () => {
  const navigate = useNavigate();
  const { hasPrivilege, loading: privilegesLoading, privilegeIds } = useUserPrivileges();
  const [couponsCount, setCouponsCount] = useState<number | null>(null);

  const HIDDEN_ADMIN_TITLES = ["Expense Categories", "Representative"];

  useEffect(() => {
    let cancelled = false;
    authenticatedFetch<{ data?: unknown[] } | unknown[]>('/api/GetCoupons?coupon=')
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const list = Array.isArray(data) ? data : (data && typeof data === 'object' && 'data' in data && Array.isArray((data as { data: unknown[] }).data) ? (data as { data: unknown[] }).data : []);
        setCouponsCount(list.length);
      })
      .catch(() => {
        if (!cancelled) setCouponsCount(0);
      });
    return () => { cancelled = true; };
  }, []);

  const allAdminItems = [
    { title: "Expense Categories", description: "Manage expense categories", icon: FolderOpen },
    { title: "Company", description: "Company settings and information", icon: Building },
    { title: "User Roles", description: "Define user roles and permissions", icon: Users },
    { title: "Representative", description: "Manage sales representatives", icon: UserCheck },
    { title: "Coupons", description: "Create and manage discount coupons", icon: Percent },
    { title: "Settings", description: "System configuration settings", icon: Settings }
  ].filter((item) => !HIDDEN_ADMIN_TITLES.includes(item.title));

  const adminItems = privilegesLoading || privilegeIds.size === 0
    ? allAdminItems
    : allAdminItems.filter((item) => {
        const requiredPrevilageId = ADMIN_CARD_PRIVILEGE[item.title];
        if (requiredPrevilageId == null) return true;
        return hasPrivilege(requiredPrevilageId);
      });

  const handleCardClick = (title: string) => {
    switch (title) {
      case "Settings":
        navigate('/settings');
        break;
      case "Company":
        navigate('/company');
        break;
      case "User Roles":
        navigate('/user-management');
        break;
      case "Coupons":
        navigate('/coupons');
        break;
      default:
        console.log(`Clicked on ${title}`);
        // TODO: Add navigation for other admin items
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6 bg-white p-3 sm:p-4 md:p-6 rounded-lg min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
        {/* <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
          Administration
        </h3> */}
        <p className="hidden md:block text-sm sm:text-base text-gray-500">Manage system configuration and settings</p>
      </div>
      
      <div className="grid grid-cols-1 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-4 gap-3 sm:gap-4 md:gap-5 lg:gap-6">
        {adminItems.map((item, index) => (
          <AdminCard
            key={index}
            title={item.title}
            description={item.description}
            icon={item.icon}
            onClick={() => handleCardClick(item.title)}
            badge={item.title === 'Coupons' ? couponsCount : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default AdministratorTab;
